import {
  CoordinateSystem,
  InputOrientation,
  LocalFrameAxes,
  MODEL_INPUT_FRAME,
  ModelOrientation,
  RealignableProject,
  TURNED_AXES_FRAME_NAME,
  Vec3,
  deriveAxesFromDefinition,
  realignInputs,
  turnPoint
} from './local-frame.util';
import { eulerFromAxes } from './coordinate-systems.util';

/** Rows of a 3x3 rotation. */
type Mat3 = [Vec3, Vec3, Vec3];

/**
 * The model orientation that turns `system` onto the global axes: its x', y'
 * and z' become X, Y and Z, so manufacturing settings chosen on the global
 * axes act along the system's axes. Null for a degenerate system.
 */
export function orientationAligning(system: CoordinateSystem, origin: number[]): ModelOrientation | null {
  const axes = deriveAxesFromDefinition(system.definition);
  if (!axes) return null;
  // The turn is the transpose of the frame's axes; eulerFromAxes takes its columns.
  const transposed: LocalFrameAxes = {
    ex: [axes.ex[0], axes.ey[0], axes.ez[0]],
    ey: [axes.ex[1], axes.ey[1], axes.ez[1]],
    ez: [axes.ex[2], axes.ey[2], axes.ez[2]]
  };
  const rotation = eulerFromAxes(transposed, 'extrinsic-xyz').map((deg) => Math.round(deg * 1e6) / 1e6 + 0);
  return { enabled: true, origin: [...origin], rotation };
}

/** A global axis direction a picked face can be turned to point along. */
export type AxisDirection = 'xp' | 'xm' | 'yp' | 'ym' | 'zp' | 'zm';

export const AXIS_DIRECTIONS: Record<AxisDirection, Vec3> = {
  xp: [1, 0, 0],
  xm: [-1, 0, 0],
  yp: [0, 1, 0],
  ym: [0, -1, 0],
  zp: [0, 0, 1],
  zm: [0, 0, -1]
};

/**
 * The model orientation that turns a face with `normal` (model coordinates) to
 * point along `target`. The turn is the shortest one from the current
 * orientation, so the model keeps its spin about the target axis. Null for a
 * zero normal.
 */
export function orientationFacing(
  current: ModelOrientation | null | undefined,
  normal: number[],
  target: Vec3,
  origin: number[]
): ModelOrientation | null {
  if (!isVec3(normal)) return null;
  const length = Math.hypot(normal[0], normal[1], normal[2]);
  if (length < 1e-9) return null;
  const turned = rotationOf(current);
  const shown = apply(turned, [normal[0] / length, normal[1] / length, normal[2] / length]);
  const total = multiply(shortestTurn(shown, target), turned);
  // eulerFromAxes takes the columns of the turn.
  const columns: LocalFrameAxes = {
    ex: [total[0][0], total[1][0], total[2][0]],
    ey: [total[0][1], total[1][1], total[2][1]],
    ez: [total[0][2], total[1][2], total[2][2]]
  };
  const rotation = eulerFromAxes(columns, 'extrinsic-xyz').map((deg) => Math.round(deg * 1e6) / 1e6 + 0);
  return { enabled: true, origin: [...origin], rotation };
}

/**
 * Moves loads, supports and points stored in the turned frame, as projects
 * saved before the turn was hidden have them, into the model's own
 * orientation, and marks the project. Returns whether anything moved.
 */
export function storeInputsInModelFrame(project: RealignableProject & { userCoordinateSystem?: InputOrientation | null }): boolean {
  const ucs = project.userCoordinateSystem;
  if (!ucs || ucs.inputFrame === MODEL_INPUT_FRAME) return false;
  ucs.inputFrame = MODEL_INPUT_FRAME;
  if (!ucs.enabled) return false;
  realignInputs(project, ucs, null, TURNED_AXES_FRAME_NAME);
  return true;
}

/** One of a coordinate system's axes. */
export type FrameAxisKey = 'ex' | 'ey' | 'ez';

/**
 * The frame turned the shortest way so its `axis` runs along `direction`; the
 * other two axes keep their spin about it. Starts from the global axes when
 * `axes` is null. Null for a zero direction.
 */
export function axesFacing(axes: LocalFrameAxes | null, axis: FrameAxisKey, direction: number[]): LocalFrameAxes | null {
  if (!isVec3(direction)) return null;
  const length = Math.hypot(direction[0], direction[1], direction[2]);
  if (length < 1e-9) return null;
  const from = axes ?? { ex: [1, 0, 0], ey: [0, 1, 0], ez: [0, 0, 1] };
  const target: Vec3 = [direction[0] / length, direction[1] / length, direction[2] / length];
  const current = from[axis];
  const currentLength = Math.hypot(current[0], current[1], current[2]);
  const turn = shortestTurn(
    [current[0] / currentLength, current[1] / currentLength, current[2] / currentLength],
    target
  );
  return { ex: apply(turn, from.ex), ey: apply(turn, from.ey), ez: apply(turn, from.ez) };
}

/**
 * The frame with its `axis` pointing the other way: half a turn about the axis
 * before it (x' about z', y' about x', z' about y'), which flips the one after
 * it too and keeps the frame right-handed.
 */
export function axesReversing(axes: LocalFrameAxes, axis: FrameAxisKey): LocalFrameAxes {
  const negate = (v: Vec3): Vec3 => [-v[0] + 0, -v[1] + 0, -v[2] + 0];
  const next: Record<FrameAxisKey, FrameAxisKey> = { ex: 'ey', ey: 'ez', ez: 'ex' };
  const turned: LocalFrameAxes = { ex: [...axes.ex], ey: [...axes.ey], ez: [...axes.ez] };
  turned[axis] = negate(axes[axis]);
  turned[next[axis]] = negate(axes[next[axis]]);
  return turned;
}

/** The rotation taking unit vector `from` onto unit vector `to` the shortest way (Rodrigues). */
function shortestTurn(from: Vec3, to: Vec3): Mat3 {
  const cos = Math.min(1, Math.max(-1, from[0] * to[0] + from[1] * to[1] + from[2] * to[2]));
  let axis: Vec3 = [
    from[1] * to[2] - from[2] * to[1],
    from[2] * to[0] - from[0] * to[2],
    from[0] * to[1] - from[1] * to[0]
  ];
  let sin = Math.hypot(axis[0], axis[1], axis[2]);
  if (sin < 1e-12) {
    if (cos > 0) return [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
    // Opposite directions: a half turn about any axis across `from`.
    const helper: Vec3 = Math.abs(from[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    axis = [
      from[1] * helper[2] - from[2] * helper[1],
      from[2] * helper[0] - from[0] * helper[2],
      from[0] * helper[1] - from[1] * helper[0]
    ];
    sin = 0;
  }
  const k = Math.hypot(axis[0], axis[1], axis[2]);
  const [x, y, z] = [axis[0] / k, axis[1] / k, axis[2] / k];
  const t = 1 - cos;
  return [
    [cos + t * x * x, t * x * y - sin * z, t * x * z + sin * y],
    [t * x * y + sin * z, cos + t * y * y, t * y * z - sin * x],
    [t * x * z - sin * y, t * y * z + sin * x, cos + t * z * z]
  ];
}

function rotationOf(orientation: ModelOrientation | null | undefined): Mat3 {
  const unturned = { ...orientation, origin: [0, 0, 0] };
  const ex = turnPoint([1, 0, 0], unturned);
  const ey = turnPoint([0, 1, 0], unturned);
  const ez = turnPoint([0, 0, 1], unturned);
  return [
    [ex[0], ey[0], ez[0]],
    [ex[1], ey[1], ez[1]],
    [ex[2], ey[2], ez[2]]
  ];
}

function multiply(a: Mat3, b: Mat3): Mat3 {
  const row = (i: number): Vec3 => [0, 1, 2].map((j) => a[i][0] * b[0][j] + a[i][1] * b[1][j] + a[i][2] * b[2][j]) as Vec3;
  return [row(0), row(1), row(2)];
}

function apply(m: Mat3, v: number[]): Vec3 {
  return [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2]
  ];
}

function isVec3(value: unknown): value is number[] {
  return Array.isArray(value) && value.length === 3 && value.every((n) => typeof n === 'number' && Number.isFinite(n));
}
